import { useEffect, useRef, Fragment } from 'react'
import { AlertTriangle, Trash2, HelpCircle } from 'lucide-react'
import { clsx } from 'clsx'

export interface ConfirmationModalProps {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'warning' | 'default'
}

const variantConfig = {
  danger: {
    icon: Trash2,
    iconBg: 'bg-red-500/10',
    iconColor: 'text-red-400',
    confirmBg: 'bg-red-600 hover:bg-red-700',
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-400',
    confirmBg: 'bg-amber-600 hover:bg-amber-700',
  },
  default: {
    icon: HelpCircle,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    confirmBg: 'bg-primary hover:bg-primary/90',
  },
}

export function ConfirmationModal({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
}: ConfirmationModalProps) {
  const confirmRef = useRef<HTMLButtonElement>(null)
  const config = variantConfig[variant]
  const Icon = config.icon

  useEffect(() => {
    if (isOpen) {
      confirmRef.current?.focus()
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onCancel])

  if (!isOpen) return null

  return (
    <Fragment>
      <div
        className="fixed inset-0 bg-black/60 z-40 animate-in"
        onClick={onCancel}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="w-full max-w-sm bg-[var(--bg-secondary)] rounded-lg shadow-modal slide-in"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
          aria-describedby="confirm-message"
        >
          <div className="p-5">
            <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center mb-3', config.iconBg)}>
              <Icon size={20} className={config.iconColor} />
            </div>
            <h3
              id="confirm-title"
              className="text-base font-semibold text-[var(--text-primary)] mb-1"
            >
              {title}
            </h3>
            <p
              id="confirm-message"
              className="text-sm text-[var(--text-secondary)] leading-relaxed"
            >
              {message}
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[var(--border-color)]">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-md hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              {cancelLabel}
            </button>
            <button
              ref={confirmRef}
              onClick={onConfirm}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium text-white rounded-md transition-colors',
                config.confirmBg
              )}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  )
}
